import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Secure Token Management Service
 * Stores authentication tokens in secure cookies with automatic expiry
 */
@Injectable({
  providedIn: 'root'
})
export class AuthTokenService {
  private readonly TOKEN_PREFIX = 'auth_token_';
  private readonly TOKEN_EXPIRY_MS = 4000; // 4 seconds (1 second buffer before server expiry)
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Store auth token in secure cookie
   * @param sessionId - Session ID for the token
   * @param token - Encrypted authentication token
   */
  setToken(sessionId: string, token: string): void {
    if (!this.isBrowser) {
      return;
    }

    const cookieName = this.getCookieName(sessionId);
    const expiryDate = new Date(Date.now() + this.TOKEN_EXPIRY_MS);

    // Create secure cookie with SameSite and Secure flags
    const cookieValue = [
      `${cookieName}=${encodeURIComponent(token)}`,
      `expires=${expiryDate.toUTCString()}`,
      'path=/',
      'SameSite=Strict', // Prevents CSRF attacks
      // Note: 'Secure' flag requires HTTPS. Enable in production!
      // window.location.protocol === 'https:' ? 'Secure' : ''
    ].filter(Boolean).join('; ');

    document.cookie = cookieValue;

    console.log(`[AuthToken] Token stored for session ${sessionId}, expires in ${this.TOKEN_EXPIRY_MS}ms`);
  }

  /**
   * Get auth token from secure cookie
   * @param sessionId - Session ID for the token
   * @returns Token if valid and not expired, null otherwise
   */
  getToken(sessionId: string): string | null {
    if (!this.isBrowser) {
      return null;
    }

    const cookieName = this.getCookieName(sessionId);
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === cookieName) {
        const token = decodeURIComponent(value);
        console.log(`[AuthToken] Token retrieved for session ${sessionId}`);
        return token;
      }
    }

    console.log(`[AuthToken] No token found for session ${sessionId}`);
    return null;
  }

  /**
   * Check if a valid token exists for the session
   * @param sessionId - Session ID to check
   * @returns true if valid token exists, false otherwise
   */
  hasValidToken(sessionId: string): boolean {
    return this.getToken(sessionId) !== null;
  }

  /**
   * Remove auth token (logout/cleanup)
   * @param sessionId - Session ID for the token to remove
   */
  removeToken(sessionId: string): void {
    if (!this.isBrowser) {
      return;
    }

    const cookieName = this.getCookieName(sessionId);
    // Set expiry to past date to delete cookie
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict`;

    console.log(`[AuthToken] Token removed for session ${sessionId}`);
  }

  /**
   * Clear all auth tokens (useful for cleanup)
   */
  clearAllTokens(): void {
    if (!this.isBrowser) {
      return;
    }

    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const [name] = cookie.trim().split('=');
      if (name.startsWith(this.TOKEN_PREFIX)) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict`;
      }
    }

    console.log('[AuthToken] All tokens cleared');
  }

  /**
   * Get the cookie name for a session
   * @param sessionId - Session ID
   * @returns Cookie name
   */
  private getCookieName(sessionId: string): string {
    return `${this.TOKEN_PREFIX}${sessionId}`;
  }
}
